import {
  App,
  Produktlinie,
  selectIndicesOfDeactivatedProduktlinien,
  TierartEnum,
  UebergreifendItem,
} from '@ps20-frontend/frontend-models';
import {
  Heilbehandlungsschutz,
  Selbstbeteiligung,
  TaasTarifrisikoschluesselTKV_V2,
} from '@ps20/bff-models';
import {
  determineTkvV2Produkttabelle,
  isNeugeschaeft,
  ProdukttabelleEnum,
  TARIF_CHECKED,
} from '@ps20-frontend/shop-integration-redux';

export type TkvV2CoverageType = 'opSchutz' | 'vollschutz';

export interface TkvV2Zusatzoption {
  name: string;
  isSelected: boolean;
  bruttopreis?: number;
  visible?: boolean;
  id?: string | null;
  title?: string;
  beschreibung?: string;
  info?: string;
  icon?: string;
  disabled?: boolean;
  isInclusive?: boolean;
}

export interface TkvV2Produktlinie {
  produktName: string;
  kurztext: string;
  selected: boolean;
  deactivated?: boolean;
  bruttopreis?: number;
  tarifBezeichnung?: string;
  zusatzoptionen?: TkvV2Zusatzoption[];
}

export interface TkvV2Vorvertragsnummer {
  selbstbeteiligung?: Selbstbeteiligung;
  verkaufsangebotKurzname?: string;
  vorbelegungForBeibehaltung?: { preisverzeichnis?: number } | null;
}

export interface TkvV2ChildState {
  angebotsId?: string;
  produkt?: { produktlinien?: TkvV2Produktlinie[]; uebergreifend?: unknown[] } | null;
  vertragsoptionen?: {
    selbstbeteiligung?: Selbstbeteiligung;
    coverageType?: TkvV2CoverageType;
    zahlungsweise?: string;
    hasWechselbaustein?: boolean;
    verkaufsangebotKurzname?: string;
    vorvertragsNummern?: TkvV2Vorvertragsnummer[] | null;
  } | null;
  versicherungsnehmer?: {
    animalType?: TierartEnum;
    wantsCoverageUpgrade?: boolean;
    useExistingWechselbaustein?: boolean | null;
  } | null;
  transientState?: {
    tarifierungIsInProgress?: boolean;
    productLoadingIsInProgress?: boolean;
    expectedProdukttabelle?: string | null;
  } | null;
}

export const TKV_V2X_VERKAUFSANGEBOTE: ReadonlySet<string> = new Set([
  'TM009',
  'TM010',
  'TM011',
  'TM012',
  'TM013',
  'TM014',
  'TM015',
  'TM016',
  'TM017',
  'TM018',
  'TM019',
  'TM020',
  'TM021',
  'TM022',
  'TM023',
  'TM024',
  'TM025',
  'TM026',
  'TM027',
  'TM028',
  'TM029',
  'TM030',
  'TM031',
  'TM032',
]);

export const TKV_V2_SELBSTBETEILIGUNG_OPTIONS: readonly Selbstbeteiligung[] = [
  Selbstbeteiligung.SELBSTBETEILIGUNG_0000,
  Selbstbeteiligung.SELBSTBETEILIGUNG_10_PROZENT,
  Selbstbeteiligung.SELBSTBETEILIGUNG_20_PROZENT,
];

export const tkvV2SelectedProduktlinie = (
  state: TkvV2ChildState | null | undefined,
): TkvV2Produktlinie | null =>
  state?.produkt?.produktlinien?.find(produktlinie => produktlinie.selected) ?? null;

export const tkvV2SelectedPreis = (state: TkvV2ChildState | null | undefined): number | null =>
  tkvV2SelectedProduktlinie(state)?.bruttopreis ?? null;

export const tkvV2Selbstbeteiligung = (
  state: TkvV2ChildState | null | undefined,
): Selbstbeteiligung | null => state?.vertragsoptionen?.selbstbeteiligung ?? null;

export const tkvV2CoverageType = (
  state: TkvV2ChildState | null | undefined,
): TkvV2CoverageType | null => state?.vertragsoptionen?.coverageType ?? null;

export const tkvV2Zusatzoptionen = (
  state: TkvV2ChildState | null | undefined,
): TkvV2Zusatzoption[] => tkvV2SelectedProduktlinie(state)?.zusatzoptionen ?? [];

type TkvV2State = TkvV2ChildState | null | undefined;

export const tkvV2Produktlinien = (state: TkvV2State): Produktlinie[] =>
  (state?.produkt?.produktlinien ?? []) as unknown as Produktlinie[];

export const tkvV2Uebergreifend = (state: TkvV2State): UebergreifendItem[] =>
  (state?.produkt?.uebergreifend ?? []) as UebergreifendItem[];

export const tkvV2SelectedIndex = (state: TkvV2State): number =>
  state?.produkt?.produktlinien?.findIndex(produktlinie => produktlinie.selected) ?? -1;

const firstVorvertrag = (state: TkvV2State): TkvV2Vorvertragsnummer | null =>
  state?.vertragsoptionen?.vorvertragsNummern?.[0] ?? null;

export const tkvV2VorvertragIndex = (state: TkvV2State): number => {
  const kurzname = firstVorvertrag(state)?.verkaufsangebotKurzname;
  if (!kurzname) {
    return -1;
  }
  return (
    state?.produkt?.produktlinien?.findIndex(produktlinie => produktlinie.kurztext === kurzname) ??
    -1
  );
};

export const tkvV2DeactivatedIndices = (state: TkvV2State): number[] =>
  selectIndicesOfDeactivatedProduktlinien({
    produkt: state?.produkt as unknown as Parameters<
      typeof selectIndicesOfDeactivatedProduktlinien
    >[0]['produkt'],
  });

export const tkvV2AnyLinieDeactivated = (state: TkvV2State): boolean =>
  !!state?.produkt?.produktlinien?.some(produktlinie => produktlinie.deactivated);

const isTkv20 = (state: TkvV2State): boolean => {
  const preisverzeichnis = firstVorvertrag(state)?.vorbelegungForBeibehaltung?.preisverzeichnis;
  return preisverzeichnis != null && preisverzeichnis < 100;
};

export const tkvV2PossibleSelbstbeteiligungen = (state: TkvV2State): Selbstbeteiligung[] => {
  if (isTkv20(state) || state?.versicherungsnehmer?.wantsCoverageUpgrade === true) {
    return [...TKV_V2_SELBSTBETEILIGUNG_OPTIONS];
  }
  switch (firstVorvertrag(state)?.selbstbeteiligung) {
    case Selbstbeteiligung.SELBSTBETEILIGUNG_10_PROZENT:
      return [
        Selbstbeteiligung.SELBSTBETEILIGUNG_10_PROZENT,
        Selbstbeteiligung.SELBSTBETEILIGUNG_20_PROZENT,
      ];
    case Selbstbeteiligung.SELBSTBETEILIGUNG_20_PROZENT:
      return [Selbstbeteiligung.SELBSTBETEILIGUNG_20_PROZENT];
    default:
      return [...TKV_V2_SELBSTBETEILIGUNG_OPTIONS];
  }
};

export const tkvV2AnimalType = (state: TkvV2State): TierartEnum | null =>
  state?.versicherungsnehmer?.animalType ?? null;

export const tkvV2Produkttabelle = (state: TkvV2State): ProdukttabelleEnum =>
  determineTkvV2Produkttabelle(tkvV2AnimalType(state), tkvV2CoverageType(state) === 'vollschutz');

export const tkvV2Zahlweise = (state: TkvV2State): string =>
  state?.vertragsoptionen?.zahlungsweise?.toLowerCase() ?? '';

export const tkvV2ShowsZusatzoptionen = (state: TkvV2State): boolean => {
  if (isNeugeschaeft(state?.vertragsoptionen as Parameters<typeof isNeugeschaeft>[0])) {
    return true;
  }
  const kurzname = state?.vertragsoptionen?.verkaufsangebotKurzname;
  if (kurzname !== undefined && TKV_V2X_VERKAUFSANGEBOTE.has(kurzname)) {
    return (
      tkvV2Zusatzoptionen(state).some(
        option => option.name === TaasTarifrisikoschluesselTKV_V2.TW,
      ) && !!state?.vertragsoptionen?.hasWechselbaustein
    );
  }
  return true;
};

export const tkvV2DisplaysBadgeInclusive = (state: TkvV2State): boolean =>
  !!state?.vertragsoptionen?.hasWechselbaustein &&
  state?.versicherungsnehmer?.useExistingWechselbaustein === true;

export const tkvV2DisplaysBadgePositive = (state: TkvV2State): boolean =>
  !!state?.vertragsoptionen?.hasWechselbaustein &&
  state?.versicherungsnehmer?.useExistingWechselbaustein === false;

export const tkvV2SummaryZusatzoptionen = (state: TkvV2State): TkvV2Zusatzoption[] => {
  const keepsWechselbaustein = tkvV2DisplaysBadgeInclusive(state);
  return tkvV2Zusatzoptionen(state).filter(
    option =>
      option.isSelected &&
      !(keepsWechselbaustein && option.name === TaasTarifrisikoschluesselTKV_V2.TW),
  );
};

export const tkvV2TotalPrice = (state: TkvV2State): number | null => {
  const linePrice = tkvV2SelectedPreis(state);
  if (linePrice === null) {
    return null;
  }
  return tkvV2SummaryZusatzoptionen(state).reduce(
    (sum, option) => sum + (option.bruttopreis ?? 0),
    linePrice,
  );
};

export const tkvV2ProductLoading = (state: TkvV2State): boolean =>
  !!state?.transientState?.productLoadingIsInProgress ||
  state?.transientState?.expectedProdukttabelle != null;

export const loadsProductTable = (appId: App, state: object | undefined): boolean =>
  appId === App.TKV_V2 && tkvV2ProductLoading(state as TkvV2State);

export const tkvV2LoadsTableOfCoverage = (state: TkvV2State): boolean =>
  state?.transientState?.productLoadingIsInProgress === true &&
  state?.transientState?.expectedProdukttabelle === tkvV2Produkttabelle(state);

export const tkvV2Actions = {
  selbstbeteiligungChanged: (selbstbeteiligung: Selbstbeteiligung) => ({
    type: 'SELBSTBETEILIGUNG_CHANGED' as const,
    payload: selbstbeteiligung,
  }),
  coverageTypeChanged: (coverageType: TkvV2CoverageType) => ({
    type: 'COVERAGE_TYPE_CHANGED' as const,
    payload: coverageType,
  }),
  heilbehandlungsschutzChanged: (heilbehandlungsschutz: Heilbehandlungsschutz) => ({
    type: 'HEILBEHANDLUNGSSCHUTZ_CHANGED' as const,
    payload: heilbehandlungsschutz,
  }),
  tarifChecked: (index: number) => ({
    type: TARIF_CHECKED as typeof TARIF_CHECKED,
    payload: { index },
  }),
  zusatzoptionChecked: (name: string) => ({
    type: 'ZUSATZOPTION_CHECKED' as const,
    payload: { name },
  }),
  zusatzoptionUnchecked: (name: string) => ({
    type: 'ZUSATZOPTION_UNCHECKED' as const,
    payload: { name },
  }),
  preiseRequested: () => ({ type: 'PREISE_REQUESTED' as const }),
  tarifwechselIncluded: (included: boolean) => ({
    type: 'TARIFWECHSEL_INCLUDED' as const,
    payload: included,
  }),
};
